import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router } from '@angular/router';
import { UserLoginService } from '../service/user-login.service';
import { UserProfile } from '../types';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PosthogService } from '../service/posthog.service';
import { CanActivateResponse } from '../types/guard';

/**
 * Checks if the user has access to a feature flag. This is evaluated from the user branch access and the feature flag payload.
 * If the user does not have access, they are redirected to the case tracking page.
 *
 * Special Cases:
 *  - If the user is an admin, they are allowed to access the route regardless of the feature flag.
 *  - If the Posthog service is not ready, the guard will return false, preventing access to the route.
 *
 * @remarks This requires a route data property `featureFlagName` to be set, which is the name of the feature flag to check.
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagGuard implements CanActivate, CanActivateChild {
  private userService = inject(UserLoginService);
  private router = inject(Router);
  private posthogService = inject(PosthogService);

  canActivate(route: ActivatedRouteSnapshot): Observable<CanActivateResponse> | CanActivateResponse {
    const user = this.userService.currentProfile();
    if (!user) {
      return this.userService.currentProfileLoaded$.pipe(
        map((user) => {
          return this.evaluate(route.data.featureFlagName, user);
        }),
      );
    }

    return this.evaluate(route.data.featureFlagName, user);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot): Observable<CanActivateResponse> | CanActivateResponse {
    return this.canActivate(childRoute);
  }

  evaluate(featureFlagName: string, user: UserProfile): CanActivateResponse {
    if (!featureFlagName || this.userService.currentUserIsAdmin()) {
      return true;
    }

    if (!this.posthogService.isReady()) {
      return false;
    }

    const hasAccess = this.posthogService.evaluateRouteFeatureFlag(featureFlagName, user);
    if (!hasAccess) {
      return this.router.createUrlTree(['home']);
    }

    return true;
  }
}
